import { useRouter } from "next/router";
import { graphql, useFragment } from "react-relay";
import type {
  EventCardProfessionalData,
  EventCardType,
} from "@olivahealth/oli-ui";
import SessionStatus from "@olivahealth/graphql-server/src/domain/value-objects/SessionStatus";
import { getUserFullName } from "@olivahealth/utils/helpers/user";
import CareType from "@olivahealth/business/src/Core/domain/value-objects/CareType";
import tokens from "@olivahealth/oli-ui/theme/tokens";
import PaymentStatus from "@olivahealth/graphql-server/src/domain/value-objects/PaymentStatus";
import { isActive } from "@olivahealth/utils/helpers/appointment";
import {
  formatDate,
  formatDateTime,
  formatTimeRange,
} from "../../../utils/date/format";
import { OlivaHook } from "../../../hooks/OlivaHook";
import useTranslation from "../../../hooks/useTranslation";
import {
  useSessionCard_session$data as FragmentData,
  useSessionCard_session$key as Fragment,
} from "./__generated__/useSessionCard_session.graphql";

const useSessionCardFragment = graphql`
  fragment useSessionCard_session on SessionResponse {
    __typename
    ... on Session {
      id
      type
      number
      checkoutUrl
      hasPendingCheckInSurvey
      status
      endDate
      startDate
      careType
      professional {
        name
        surname
        clinicalProfile {
          image
          professionalTitle
        }
      }
      employee {
        name
        surname
      }
      therapy {
        careType
      }
      paymentStatus
    }
  }
`;

interface UseSessionCardData {
  buttonLabel: string;
  sessionStatus: {
    label: string;
    color: string;
  };
  careType: CareType | null;
  eventDate: string;
  eventType: EventCardType;
  isAwaitingPayment: boolean;
  checkoutUrl: string | null;
  isSessionActive: boolean;
  pendingProgressCheckSurvey: boolean;
  professional?: EventCardProfessionalData;
  sessionId: string;
  timeRange: string;
  isCancelled: boolean;
  eventShortDate: string;
}

interface UseSessionCardEffects {
  handleClickButton: () => void;
}

type UseSessionCard = OlivaHook<UseSessionCardData, UseSessionCardEffects>;

export default function useSessionCard(session: Fragment): UseSessionCard {
  const { t } = useTranslation("employeeOneToOnePage", {
    keyPrefix: "yourSessions",
  });

  const data = useFragment<Fragment>(useSessionCardFragment, session);
  const { push } = useRouter();

  const sessionId = data.__typename === "Session" ? data.id : "";
  const isSession = data.__typename === "Session";
  const isSessionActive = isSession && isActive(data.startDate);
  const startDate = isSession ? data.startDate : null;
  const endDate = isSession ? data.endDate : null;
  const pendingProgressCheckSurvey =
    data.__typename === "Session" &&
    data.hasPendingCheckInSurvey &&
    ["ACTIVE", "SCHEDULED"].includes(data.status);
  const timeRange =
    startDate && endDate ? formatTimeRange(startDate, endDate) : "";

  const eventDate = isSession ? formatDate(data.startDate) : "";
  const eventShortDate = isSession ? formatDateTime(data.startDate) : "";

  const eventType = getEventType(data);
  const professional = getProfessionalInfo(data);
  const isAwaitingPayment =
    data.__typename === "Session" &&
    data.status === SessionStatus.AWAITING_PAYMENT;
  const isCancelled =
    (data.__typename === "Session" &&
      data.status === SessionStatus.CANCELLED) ||
    (data.__typename === "Session" &&
      data.status === SessionStatus.CANCELLED_LATE);

  const isRefunded =
    data.__typename === "Session" &&
    data.paymentStatus === PaymentStatus.REFUNDED;

  const handleClickButton = () => {
    if (isSessionActive) {
      push(`/sessions/${sessionId}/talk`);
    } else if (isAwaitingPayment && data.checkoutUrl) {
      push(data.checkoutUrl);
    } else {
      push(`/sessions/${sessionId}`);
    }
  };

  const getSessionStatus = () => {
    if (isSessionActive) {
      return {
        label: t("sessionCard.sessionStatus.active"),
        color: tokens.colors["status-success"][200],
      };
    }

    if (isAwaitingPayment) {
      return {
        label: t("sessionCard.sessionStatus.awaitingPayment"),
        color: tokens.colors["status-yellow"][500],
      };
    }

    if (isCancelled) {
      return {
        label: t("sessionCard.sessionStatus.cancelled"),
        color: tokens.colors["status-red"][500],
      };
    }

    if (isRefunded) {
      return {
        label: t("sessionCard.sessionStatus.refunded"),
        color: tokens.colors["status-success"][200],
      };
    }

    return {
      label: t("sessionCard.sessionStatus.upcoming"),
      color: tokens.colors["status-success"][200],
    };
  };

  const getButtonLabel = () => {
    if (isAwaitingPayment) {
      return t("sessionCard.buttonLabelPayNow");
    }

    if (isSession && isSessionActive) {
      return t("sessionCard.buttonLabelJoinCall");
    }

    if (pendingProgressCheckSurvey) {
      return t("sessionCard.buttonLabelPendingProgressCheck");
    }

    return t("sessionCard.buttonLabelViewDetails");
  };

  return {
    status: "success",
    data: {
      buttonLabel: getButtonLabel() ?? "",
      sessionStatus: getSessionStatus(),
      eventDate,
      eventShortDate,
      eventType,
      isAwaitingPayment,
      checkoutUrl:
        data.__typename === "Session" ? (data.checkoutUrl ?? null) : null,
      isSessionActive,
      pendingProgressCheckSurvey,
      professional,
      sessionId: isSession ? data.id : "",
      timeRange,
      isCancelled,
      careType: data.__typename === "Session" ? (data.careType ?? null) : null,
    },
    effects: {
      handleClickButton,
    },
    ...(data.__typename === "%other" && {
      error: { reason: "[useSessionCard] Error fetching session" },
    }),
  };
}

function getEventType(data: FragmentData): EventCardType {
  if (data.__typename === "Session") {
    return data.type === "DROP_IN" ? "check-in" : "therapy";
  }
  return "therapy";
}

function getProfessionalInfo(
  data: FragmentData,
): EventCardProfessionalData | undefined {
  if (data.__typename !== "Session") {
    return undefined;
  }

  return {
    name: getUserFullName(data.professional),
    image: data.professional?.clinicalProfile?.image ?? null,
    title: data.professional?.clinicalProfile?.professionalTitle ?? null,
  };
}
